/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { select, Store } from '@ngrx/store';
import {
  ApplyData,
  GetAnnotationSettingsFromXMLFile,
  GetFittingsTagLocationPath,
  GetRevitInformation,
  GetTagLocationPath,
  IsCurrentSelectionValid,
  SaveAnnotationSettingsToXmlFile,
} from './annotation-settings-actions';
import {
  selectAnnotationSettings,
  selectRevitInformation,
  selectCurrentSelectionResult,
  selectTagLocationPath,
  selectFittingsTagLocationPath,
} from './annotation-settings-selector';
import { Injectable } from '@angular/core';
import { AnnotationSettingsService } from '../../../../../utils/src/lib/sysque-backend/annotation-settings.service';
import { filter, Observable, Subject, switchMap, take } from 'rxjs';
import {
  Annotation,
  AnnotationSettings,
  AnnotationSettingsTabType,
  CurrentSelectionResult,
  RevitInformation,
  TagsState,
} from '../../models/annotation-settings';
import { IAnnotationSettingsFacade } from 'libs/sysque/toolbelt/data-access/src/lib/domain/facades-models/annotation-settings-facade-interface';
import { LocalStorageService } from '../../../../../utils/src/lib/local-storage/local-storage.service';
import { AnnotationSettingsReceiveController } from '../../receive-controllers/annotation-settings-receive-controller';
import { ReceiveMessageHandler } from '@mep/web-browser-bridge';
import { AnnotationSettingsLabelsManager } from '../../../../../utils/src/lib/common-helper/annotation-settings-labels-manager/annotation-settings-labels-manager';
import { GlobalConstants } from '../../constants/global-constants';

@Injectable({
  providedIn: 'root',
})
export class AnnotationSettingsFacade implements IAnnotationSettingsFacade {

  private annotationSettingsLoaded$ = new Subject<void>();
  
  constructor(
    private store: Store,
    private localStorageService: LocalStorageService,
    private annotationSettingsService: AnnotationSettingsService,
    private readonly receiveMessageService: ReceiveMessageHandler,
    private readonly annotationSettingsLabelsManager: AnnotationSettingsLabelsManager
  ) {
    this.receiveMessageService.registerController(new AnnotationSettingsReceiveController(localStorageService));
  }

  public getAnnotationSettings(): Observable<AnnotationSettings> {
      this.store.dispatch(GetAnnotationSettingsFromXMLFile());
      this.store.pipe(
        select(selectAnnotationSettings),
        filter(data => !!data), // Ensure that data is not empty or initial state
        take(1)
      ).subscribe(() => {
        this.annotationSettingsLoaded$.next();
      });
  
      return this.annotationSettingsLoaded$.pipe(
        switchMap(() => this.store.select(selectAnnotationSettings))
      );
  }

  public saveAnnotationSettings(annotationSettings: AnnotationSettings): void {
    this.store.dispatch(SaveAnnotationSettingsToXmlFile({ annotationSettings }));
  }

  public getRevitInformation(): void {
    this.store.dispatch(GetRevitInformation());
  }

  public getRevitInformationFromStore(): Observable<RevitInformation> {
    return this.store.select(selectRevitInformation);
  }

  getRevitVersionFromLocalStorage(): string {
    return this.localStorageService.getStorage(GlobalConstants.REVIT_VERSION);
  }

  public getTagLocationPath(): Observable<string> {
    this.store.dispatch(GetTagLocationPath());
    return this.store.select(selectTagLocationPath);
  }

  public getFittingsTagLocationPath(): Observable<string> {
    this.store.dispatch(GetFittingsTagLocationPath());
    return this.store.select(selectFittingsTagLocationPath);
  }

  public isCurrentSelectionValid(): Observable<CurrentSelectionResult> {
    this.store.dispatch(IsCurrentSelectionValid());
    return this.store.select(selectCurrentSelectionResult);
  }

  public getCurrentSelectionResult(): Observable<CurrentSelectionResult> {
    return this.store.select(selectCurrentSelectionResult);
  }

  public applyData(selectedTab: AnnotationSettingsTabType): void {
    this.store.dispatch(ApplyData({ selectedTab }));
  }

  public updateStorage(key: string, value: any): void {
    this.localStorageService.updateStorage(key, value);
  }

  public getCableTrayDataFromXml(): Annotation[] {
    return this.annotationSettingsService.getCableTrayDataFromXml();
  }

  public getConduitSettingsDataFromXml(): Annotation[] {
    return this.annotationSettingsService.getConduitSettingsDataFromXml();
  }

  public getActivePropertyByTagName(name: string, items: Annotation[]): boolean {
    return this.annotationSettingsService.getActivePropertyByTagName(name, items);
  }

  public getTagsStateFromStorageByTabName(tabName: string): TagsState {
    return this.annotationSettingsService.getTagsStateFromStorageByTabName(tabName);
  }

  public setDefaultSelectionToStorageByTabNameAndAnnotationName(
    currentXmlAnnotation: Annotation,
    tabName: string,
    annotationName: string,
    selectionName: string
  ): void {
    this.annotationSettingsService.setDefaultSelectionToStorageByTabNameAndAnnotationName(
      currentXmlAnnotation,
      tabName,
      annotationName,
      selectionName
    );
  }

  public getTagDataFromXmlByTabNameAndAnnotationName(tabName: string, tagName: string): Annotation {
    return this.annotationSettingsService.getTagDataFromXmlByTabNameAndAnnotationName(tabName, tagName);
  }

  public getOccurrencePropertyByName(name: string, data: Annotation): boolean {
    return this.annotationSettingsService.getOccurrencePropertyByName(name, data);
  }

  public getPropertiesPropertyByName(name: string, data: Annotation): boolean {
    return this.annotationSettingsService.getPropertiesPropertyByName(name, data);
  }

  public getTagDataFromLocalStorageByTagName(tagName: string): Annotation {
    return this.annotationSettingsService.getTagDataFromLocalStorageByTagName(tagName);
  }

  public getDataToLoadByTabAndAnnonationName(tabName: string, annotationName: string, dataSelectionName: string): Annotation {
    return this.annotationSettingsService.getDataToLoadByTabAndAnnonationName(
      tabName,
      annotationName,
      dataSelectionName
    );
  }

  public setActiveValueToLocalStorageByTabNameAndTagName(tabName: string, tagName: string, value: string): void {
    this.annotationSettingsService.setActiveValueToLocalStorageByTabNameAndTagName(tabName, tagName, value);
  }

  public setPlacementValueToStorage(dataObjName: string, value: string): void {
    this.annotationSettingsService.setPlacementValueToStorage(dataObjName, value);
  }

  public setOffsetValueToStorage(dataObjName: string, label: string, value: number): void {
    this.annotationSettingsService.setOffsetValueToStorage(dataObjName, label, value);
  }

  public setOccurrenceValueToStorage(dataObjName: string, value: string): void {
    this.annotationSettingsService.setOccurrenceValueToStorage(dataObjName, value);
  }

  public setPropertiesValueToStorage(dataObjName: string, label: string, value: boolean): void {
    this.annotationSettingsService.setPropertiesValueToStorage(dataObjName, label, value);
  }

  public setIgnoreStraightsValueToStorage(dataObjName: string, value: number): void {
    this.annotationSettingsService.setIgnoreStraightsValueToStorage(dataObjName, value);
  }

  public setTagPathValueToStorage(dataObjName: string, value: string): void {
    this.annotationSettingsService.setTagPathValueToStorage(dataObjName, value);
  }

  public setFittingTagPathValueToStorage(dataObjName: string, value: string): void {
    this.annotationSettingsService.setFittingTagPathValueToStorage(dataObjName, value);
  }

  public loadImageByTabAndState(tabName: string, tagState: TagsState, data: Annotation[]): void {
    this.annotationSettingsLabelsManager.loadImageByTabAndState(tabName, tagState, data);
  }
}
